/**
 * Output preview table. column-mapping-ui spec, "Output preview table": columns
 * are the resolved system fields (post-split, post-transform, canonical names)
 * and cells carry validation flags (ok, warning, error) reflecting the current
 * mapping and transforms.
 */
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { DataTable } from '@ooh/ui/components/data-table';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { Badge } from '@ooh/ui/components/badge';
import { cn } from '@ooh/ui/lib/utils';
import { usePersistedDataTableColumns } from '@ooh/ui/lib/use-persisted-data-table-columns';

export type PreviewRowStatus = 'ok' | 'warning' | 'error';

export interface PreviewCellFlag {
  message: string;
  severity: 'warning' | 'error';
}

export interface PreviewCell {
  value: string;
  flag?: PreviewCellFlag;
  /** Render the value in monospace, e.g. coordinates. */
  mono?: boolean;
}

export interface MappingTransformsPreviewRow {
  id: string;
  status: PreviewRowStatus;
  /** Keyed by column id. */
  cells: Record<string, PreviewCell>;
  /** Translated messages for row-level flags that don't resolve to any rendered column (e.g. a
   * pattern-transform mismatch on a source column that isn't itself a preview column) —
   * surfaced near the status badge so the row's warning/error status is never unexplained. */
  reasons?: string[];
}

export interface MappingTransformsPreviewColumn {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
}

export interface MappingTransformsPreviewTableProps {
  /** Fully formatted heading, e.g. "Podgląd zaimportowanych danych (3 z 68)" */
  heading: string;
  columns: MappingTransformsPreviewColumn[];
  rows: MappingTransformsPreviewRow[];
}

const STATUS_BADGE_VARIANT: Record<PreviewRowStatus, 'success' | 'warning' | 'destructive'> = {
  ok: 'success',
  warning: 'warning',
  error: 'destructive',
};

const STATUS_ICON: Record<PreviewRowStatus, React.ReactNode> = {
  ok: <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />,
  warning: <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />,
  error: <XCircle className="h-4 w-4" strokeWidth={1.8} />,
};

function StatusBadge({ status }: { status: PreviewRowStatus }) {
  return (
    <Badge variant={STATUS_BADGE_VARIANT[status]} className="h-6 w-6 justify-center p-0">
      {STATUS_ICON[status]}
    </Badge>
  );
}

/** Status badge plus, when present, the row's orphaned-flag reasons (item #3) rendered right
 * below it. No fixed `width` on this column (see ColumnDef.width doc — a fixed width hides
 * overflow), so the reason text can wrap instead of being clipped. */
function StatusCell({ row }: { row: MappingTransformsPreviewRow }) {
  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <StatusBadge status={row.status} />
      {row.reasons && row.reasons.length > 0 ? (
        <span className="text-xs leading-tight text-muted-foreground text-center max-w-36">
          {row.reasons.join(' · ')}
        </span>
      ) : null}
    </div>
  );
}

function buildColumns(
  columns: MappingTransformsPreviewColumn[],
): ColumnDef<MappingTransformsPreviewRow>[] {
  return [
    {
      id: 'status',
      label: '',
      align: 'center',
      resizable: false,
      renderCell: (row) => <StatusCell row={row} />,
    },
    ...columns.map((col) => ({
      id: col.id,
      label: col.label,
      align: col.align ?? ('left' as const),
      renderCell: (row: MappingTransformsPreviewRow) => {
        const cell = row.cells[col.id];
        if (!cell) return null;
        return (
          <div className="flex flex-col gap-1 items-start">
            <span
              className={cn(
                cell.mono && 'font-mono text-xs',
                cell.flag?.severity === 'error' && 'text-destructive',
              )}
            >
              {cell.value}
            </span>
            {cell.flag ? (
              <Badge
                variant={cell.flag.severity === 'error' ? 'destructive' : 'warning'}
                className="w-fit whitespace-nowrap"
              >
                {cell.flag.message}
              </Badge>
            ) : null}
          </div>
        );
      },
    })),
  ];
}

export function MappingTransformsPreviewTable({
  heading,
  columns,
  rows,
}: MappingTransformsPreviewTableProps) {
  const tableColumns = buildColumns(columns);
  const persistedTable = usePersistedDataTableColumns(tableColumns, 'ooh:import-mapping-preview');

  return (
    <div data-testid="preview-table-section">
      <h2 className="text-base font-semibold mb-3">{heading}</h2>
      <DataTable
        columns={persistedTable.columns}
        data={rows}
        getRowKey={(row) => row.id}
        onColumnResizeEnd={persistedTable.onColumnResizeEnd}
      />
    </div>
  );
}
